import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";

import { useTranslation } from "@/core/i18n";
import { getPlatformLocationByLocationIdConcerns } from "api/methods";
import { PersonalisationConcerns } from "modules/personalisation/components/PersonalisationConcerns";
import { MeetYourAIAdvisorContainer_PATH } from "modules/personalisation/containers/MeetYourAIAdvisorContainer";
import { PersonalisationAdvisorBenefitsContainer_PATH } from "modules/personalisation/containers/PersonalisationAdvisorBenefitsContainer";
import { usePersonalisationData } from "modules/personalisation/context/PersonalisationDataContext";
import { usePersonalisationLeave } from "modules/personalisation/context/PersonalisationLeaveContext";
import { groupConcernsByCategory as groupConcernsByCategoryAndFilterByGender } from "modules/personalisation/utils/concernGrouping";
import { hasSkincareConcerns } from "modules/personalisation/utils/skincareConcerns";

import { PersonalisationHeading } from "../components/PersonalisationHeading";

import { HeritageContainer_PATH } from "./HeritageContainer";
import { PreparingScreenContainer_PATH } from "./PreparingScreenContainer";

export const ImproveConcernsContainer_PATH = "/personalise/concerns" as const;

export const ConcernsLoadingIndicator = () => {
  const { t } = useTranslation();
  return (
    <div className="flex gap-2">
      <div className="h-6 w-6 animate-spin rounded-full border-2 border-gray-300 border-t-brand-color"></div>
      <PersonalisationHeading size="xs">
        {t("improveConcernsContainer.loadingConcerns")}
      </PersonalisationHeading>
    </div>
  );
};

export function ImproveConcernsContainer() {
  const navigate = useNavigate();
  const { concerns, setConcernsAnswered, skipConcerns, advisor, user } = usePersonalisationData();
  const { openConfirmLeave } = usePersonalisationLeave();

  const { data: apiConcerns, isPending: isLoading } = useQuery({
    ...getPlatformLocationByLocationIdConcerns.keyed({
      locationId: user.platformLocation?.id as string,
    }),
    staleTime: 1000 * 60 * 5,
  });

  const concernsData = apiConcerns?.concerns ?? [];
  const concernCategories = groupConcernsByCategoryAndFilterByGender(concernsData, user.gender);

  const savedConcernIds = concerns.state === "answered" ? concerns.value : [];
  const initialSelections = concernsData.filter((concern) => savedConcernIds.includes(concern.id));

  // Check if practice offers any skincare concerns to determine navigation
  // see https://app.asana.com/1/1200167319116526/project/1210177794071597/task/1211771619790922
  const practiceHasSkincareConcerns = hasSkincareConcerns(concernsData);
  const nextPath = practiceHasSkincareConcerns
    ? HeritageContainer_PATH
    : PreparingScreenContainer_PATH;

  return (
    <PersonalisationConcerns
      concernCategories={concernCategories}
      initialSelections={initialSelections}
      progressPercentage={17}
      onContinue={(selections) => {
        setConcernsAnswered(selections.map((selection) => selection.id));
        navigate(nextPath);
      }}
      onClose={openConfirmLeave}
      onBack={() => {
        if (advisor.state === "answered") {
          navigate(MeetYourAIAdvisorContainer_PATH);
        } else {
          navigate(PersonalisationAdvisorBenefitsContainer_PATH);
        }
      }}
      onSkip={() => {
        skipConcerns();
        navigate(nextPath);
      }}
      prefix={isLoading && <ConcernsLoadingIndicator />}
    />
  );
}
